package dev.lingvo.shared

import kotlinx.serialization.Serializable

/**
 * How often a subscription bills.
 *
 * Yearly is priced as ten months for twelve, and that ratio is **derived here, never typed into a
 * price list**. A hardcoded annual figure is a number that has to be changed in two places every time
 * a plan's price moves, and the one that gets forgotten is the one a customer is charged.
 */
@Serializable
enum class BillingInterval(val displayName: String, val months: Int) {
    MONTH("Monthly", 1),

    /** Twelve months of service for ten months of money. */
    YEAR("Yearly", 12),
}

/** What one seat costs for one billing period on this plan. */
fun Plan.pricePerSeat(interval: BillingInterval): Int = when (interval) {
    BillingInterval.MONTH -> pricePerSeat
    BillingInterval.YEAR -> pricePerSeat * 10
}

/**
 * An amount of money, formatted by the server.
 *
 * **Never a Double, and never formatted in the browser.** Two reasons, both of which produce wrong
 * numbers on the screen where a customer decides whether to trust us with a card:
 *
 * Floating point cannot represent 0.1, so any client-side arithmetic on prices eventually shows
 * $19.999999999999998 or loses a cent on a total. Minor units are integers and integers are exact.
 *
 * And currencies do not all have two decimal places. JPY and KRW have none — ¥1900 is one thousand
 * nine hundred yen, not ¥19.00 — so a generic "divide by 100" is a hundredfold error in the customer's
 * favour or ours, depending on which way it falls. The server knows the currency's exponent; the
 * browser renders [display] and does no arithmetic at all.
 */
@Serializable
data class Money(
    /** The amount in the currency's smallest unit: cents for USD, whole yen for JPY. */
    val amountMinor: Long,
    /** ISO 4217, upper case. */
    val currency: String,
    /** Ready to render. The client must not attempt to produce this itself. */
    val display: String,
) {
    companion object {
        /**
         * Currencies with no minor unit.
         *
         * Covers the zero-decimal Paddle payment currencies, plus the existing ISK compatibility.
         * HUF and TWD use two decimals. Other codes retain the two-decimal fallback; this helper
         * does not establish payment support for arbitrary currencies.
         */
        private val ZERO_DECIMAL = setOf("JPY", "KRW", "VND", "CLP", "ISK")

        fun of(amountMinor: Long, currency: String): Money {
            val code = currency.uppercase()
            val display = if (code in ZERO_DECIMAL) {
                "$amountMinor ${code}"
            } else {
                // Split digits rather than taking abs(Long.MIN_VALUE), which overflows. Keeping
                // the sign separately also preserves credits smaller than one major unit.
                val digits = amountMinor.toString().removePrefix("-").padStart(3, '0')
                val sign = if (amountMinor < 0) "-" else ""
                val symbol = SYMBOLS[code]
                val amount = "$sign${digits.dropLast(2)}.${digits.takeLast(2)}"
                if (symbol != null) "$symbol$amount" else "$amount $code"
            }
            return Money(amountMinor = amountMinor, currency = code, display = display)
        }

        private val SYMBOLS = mapOf("USD" to "$", "EUR" to "€", "GBP" to "£")
    }
}

/**
 * A quote for a change that costs money, or an honest statement that we cannot quote it.
 *
 * [available] exists because "we cannot work out the price right now" is a **first-class state, not
 * an error**. The processor being slow must not block a role change — the customer is entitled to
 * make it — but nor may the interface invent a number. It says so, and the change still goes
 * through.
 */
@Serializable
data class ProrationPreviewDto(
    val available: Boolean,
    /** What will be charged now, pro rata. Null when [available] is false. */
    val chargedNow: Money? = null,
    /** What the recurring total becomes. Null when [available] is false. */
    val nextPeriod: Money? = null,
    val seatsBefore: Int = 0,
    val seatsAfter: Int = 0,
    /** Why no quote, in words a customer can act on. Set exactly when [available] is false. */
    val unavailableReason: String? = null,
)
